// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// REPEAT SUPPLIERS — a leaderboard whose name-merge is auditable in the UI.
//
// Supplier names are free text with no entity id, so "Woolworths Ltd" and
// "Woolworths Limited" are two strings for one company. Without normalisation a
// ranking is simply wrong; WITH normalisation it is only trustworthy if the
// reader can see what was merged. So every row expands to list the exact raw
// strings it combines. Disclosure is part of the chart, not a footnote.
//
// And the ranking is explicitly NOT a danger ranking. Recall counts track
// company size and reporting diligence at least as much as product safety, and
// a company that recalls promptly ranks worse than one that sits on a problem.
// That warning sits above the table, not below it.

import { getBundle } from '../data';
import { esc, num } from '../format';
import { navigate } from '../router';
import { markTip } from '../components/svg';

const TOP = 40;

export function renderSuppliers(host: HTMLElement): void {
  const { stats, hazardById, meta } = getBundle();
  const rows = stats.suppliers.filter((s) => s.key !== '(not stated)').slice(0, TOP);
  // From the pipeline, computed over ALL suppliers — the payload ships only the
  // top 300, so counting here would have described the slice, not the register.
  const merged = stats.supplierMeta?.multiVariant
    ?? stats.suppliers.filter((s) => s.variants.length > 1).length;

  host.innerHTML = `
    <div class="view-head">
      <h2>The companies that appear most often</h2>
      <p>The ${TOP} suppliers with the most recalls on the register, after merging the different ways each
      company's name is written. ${num(merged)} companies were written more than one way — expand any row to
      see exactly which strings were combined into it.</p>
    </div>

    <div class="note" style="margin-bottom:var(--space-lg)">
      <strong>This is not a ranking of which companies are dangerous.</strong> A big retailer sells far more
      products than a small importer and will recall more of them, and a company that reports problems promptly
      appears here more often than one that does not. Read it as “who shows up in this register”, which is the
      only thing recall counts can honestly measure.
    </div>

    <div class="table-scroll">
      <table>
        <caption class="visually-hidden">Suppliers ranked by number of recalls, with hazard mix and years active</caption>
        <thead>
          <tr>
            <th scope="col" style="width:2.5rem">#</th>
            <th scope="col">Supplier</th>
            <th scope="col" class="num">Recalls</th>
            <th scope="col">Hazard mix</th>
            <th scope="col" class="num">Active</th>
            <th scope="col" style="width:6.5rem">Names merged</th>
          </tr>
        </thead>
        <tbody id="rows"></tbody>
      </table>
    </div>
    <p class="small muted" style="margin-top:var(--space-md)">
      Merging is deliberately conservative: it strips punctuation, case and legal suffixes (Pty Ltd, Limited,
      Australia) and nothing else. Two companies with similar but distinct names are never combined, because
      guessing would put one company's safety record under another company's name.
    </p>
  `;

  const tbody = host.querySelector<HTMLElement>('#rows')!;

  rows.forEach((s, i) => {
    const tr = document.createElement('tr');
    tr.className = 'clickable';

    const mix = document.createElement('td');
    mix.append(hazardStrip(s.byHazard, s.total, hazardById));

    tr.innerHTML = `
      <td class="num muted">${i + 1}</td>
      <td><div class="truncate" style="max-width:22rem;font-weight:600">${esc(s.display)}</div></td>
      <td class="num">${num(s.total)}</td>
      <td class="mix"></td>
      <td class="num nowrap">${s.firstYear}–${s.lastYear}</td>
      <td>${s.variants.length > 1
        ? `<button type="button" class="btn btn-sm" data-variants="${i}">${s.variants.length} names</button>`
        : '<span class="muted small">one</span>'}</td>
    `;
    tr.querySelector('.mix')!.replaceWith(mix);
    mix.className = 'mix';

    tr.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('[data-variants]')) return;
      navigate('search', { s: s.key });
    });
    tbody.append(tr);

    const btn = tr.querySelector<HTMLButtonElement>('[data-variants]');
    if (btn) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const existing = tr.nextElementSibling;
        if (existing?.classList.contains('variant-row')) { existing.remove(); btn.textContent = `${s.variants.length} names`; return; }
        const vr = document.createElement('tr');
        vr.className = 'variant-row';
        vr.innerHTML = `<td colspan="6" style="background:var(--bg-elevated)">
          <div class="small"><strong>Merged from these exact strings in the register:</strong></div>
          <ul class="small" style="margin:.4rem 0 0 1.1rem">
            ${s.variants.map(([raw, n]) => `<li><span class="mono">${esc(raw)}</span> — ${num(n)} recall${n === 1 ? '' : 's'}</li>`).join('')}
          </ul></td>`;
        tr.after(vr);
        btn.textContent = 'Hide';
      });
    }
  });

  void meta;
}

/** A compact stacked bar of a supplier's hazard mix — a ranking becomes a diagnosis. */
function hazardStrip(
  byHazard: Record<number, number>,
  total: number,
  hazardById: ReturnType<typeof getBundle>['hazardById'],
): HTMLElement {
  const wrap = document.createElement('span');
  // A bare <span> ignores width entirely — it must be a block/flex box or the
  // whole strip renders as an invisible zero-height nothing.
  wrap.style.cssText = 'display:flex;width:min(220px,100%);height:14px;border-radius:3px;overflow:hidden;background:var(--bg-elevated)';

  const parts = Object.entries(byHazard)
    .map(([k, n]) => ({ hz: hazardById.get(Number(k)), n }))
    .filter((p) => p.hz)
    .sort((a, b) => b.n - a.n);

  for (const p of parts) {
    const seg = document.createElement('span');
    seg.style.cssText = `display:block;flex:0 0 ${(100 * p.n) / total}%;background:${p.hz!.colour}`;
    markTip(seg, `${p.hz!.label}: ${num(p.n)} of ${num(total)} (${((100 * p.n) / total).toFixed(0)}%)`);
    wrap.append(seg);
  }
  // A <span> maps to the `generic` role, which does not support naming — the
  // aria-label was simply dropped by every screen reader. role="img" is correct
  // here because the segments are not focusable.
  wrap.setAttribute('role', 'img');
  wrap.setAttribute('aria-label', `Hazard mix: ${parts.map((p) => `${p.hz!.label} ${p.n}`).join(', ')}`);
  return wrap;
}
